export type OwnerKind = 'trader' | 'bot' | 'remote' | 'unattributed';

export type OwnerFilterKey =
  | 'all'
  | 'unattributed'
  | `trader:${string}`
  | `bot:${string}`
  | `remote:${string}`;

export interface OwnerAttribution {
  kind: OwnerKind;
  /** Stable user/agent identifier when the relay can prove one. */
  id: string | null;
  /** Human-facing label. Never inferred from order/fill characteristics. */
  label: string | null;
  filterKey: Exclude<OwnerFilterKey, 'all'>;
}

export const UNATTRIBUTED_OWNER: OwnerAttribution = Object.freeze({
  kind: 'unattributed',
  id: null,
  label: null,
  filterKey: 'unattributed',
});

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * Convert the relay's canonical owner fields into one UI attribution object.
 *
 * The explicit ownerKind is authoritative. We do not infer an owner from a
 * ticker, label, order shape, or shared credential. If the relay says trader
 * or bot but omits the friendly label, the stable ID is shown instead; if it
 * omits the stable ID, the row stays unattributed.
 */
export function resolveOwnerAttribution(row: Record<string, unknown>): OwnerAttribution {
  if (row.ownerKind === 'trader') {
    const id = nonEmptyString(row.ownerUserId);
    if (!id) return UNATTRIBUTED_OWNER;
    return {
      kind: 'trader',
      id,
      label: nonEmptyString(row.ownerUsername) ?? `trader ${id.slice(0, 8)}`,
      filterKey: `trader:${id}`,
    };
  }

  if (row.ownerKind === 'bot') {
    const id = nonEmptyString(row.botAgentId);
    if (!id) return UNATTRIBUTED_OWNER;
    return {
      kind: 'bot',
      id,
      label: nonEmptyString(row.botLabel) ?? `bot ${id}`,
      filterKey: `bot:${id}`,
    };
  }

  // A sibling deployment's trader, mirrored via the cross-instance
  // attribution sync (shared org keys, separate DBs). Label reads
  // "JM @ vas" — trader first, instance as the qualifier.
  if (row.ownerKind === 'remote') {
    const surface = nonEmptyString(row.remoteSurface);
    const id = nonEmptyString(row.remoteUserId);
    if (!surface || !id) return UNATTRIBUTED_OWNER;
    const name = nonEmptyString(row.remoteUsername) ?? `trader ${id.slice(0, 8)}`;
    return {
      kind: 'remote',
      id,
      label: `${name} @ ${surface}`,
      filterKey: `remote:${surface}:${id}`,
    };
  }

  return UNATTRIBUTED_OWNER;
}

export function ownerMatchesFilter(owner: OwnerAttribution, filter: OwnerFilterKey): boolean {
  return filter === 'all' || owner.filterKey === filter;
}
